import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import * as L from 'leaflet';

import { DeliveryService } from 'src/app/services/delivery.service';

// The dispatch board: what every driver is doing TODAY, live.
//
// Read-only on purpose. Runs are generated from Delivery today or the Routes
// page and everything that happens at a stop is written by the driver through
// the /driver API; this screen only watches. The map on top is the same data in
// space: the drivers' last known positions and the open run's stops. Firestore snapshots keep it current without a
// refresh button — an operator leaves it open on a second monitor.
//
// NOTE on *ngFor: every list bound in the template is a plain FIELD recomputed
// when data arrives, never a getter (the four-times-frozen-dashboard rule).

interface StopRow {
    id: string;
    systemId: string;
    order: number;
    systemName: string;
    siteName: string;
    status: string;
    statusLabel: string;
    statusClass: string;
    arrivedAt: string;
    completedAt: string;
    fillCount: number;
    svinnCount: number;
    checksLabel: string;
    checksBad: boolean;
    note: string;
}

interface RunRow {
    id: string;
    routeName: string;
    driverUid: string | null;
    driverName: string;
    status: string;
    statusLabel: string;
    statusClass: string;
    stopCount: number;
    completedStopCount: number;
    progressPct: number;
    pickedUpAt: string;
    startedAt: string;
    completedAt: string;
    /** "At Eramet (stop 3)" / "Driving — last done Eramet" — from run stamps. */
    whereLabel: string;
    lastActivity: string;
    expectedLabel: string;
    issueCount: number;
    /** "covering for Ola" — a driver took this route over from its holder today. */
    coveringLabel: string;
}

// "Last seen" per driver, from driverLocations. Position only flows while the
// driver has the app open (the app tells them so), so silence means the phone
// is locked or in a pocket — normal, not alarming. It goes stale-grey after
// 15 minutes rather than pretending to be live.
interface LastSeen {
    label: string;
    mapsUrl: string;
    stale: boolean;
    lat: number;
    lng: number;
    /** Unix seconds of the fix — the map greys a marker off the same number. */
    at: number;
}

@Component({
    selector: 'app-delivery-dispatch',
    templateUrl: './dispatch.component.html',
    styleUrls: ['./dispatch.component.css']
})
export class DeliveryDispatchComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('mapEl') mapEl?: ElementRef<HTMLDivElement>;

    date = moment().format('YYYY-MM-DD');
    loading = true;
    runs: RunRow[] = [];

    expandedRunId: string | null = null;
    stops: StopRow[] = [];
    stopsLoading = false;

    lastSeen: { [driverUid: string]: LastSeen } = {};

    // ---- the map -------------------------------------------------------------
    // Where everybody is, for the selected date: one marker per driver from
    // driverLocations, plus the stops of the open run in driving order. It is
    // honest about what it knows — positions only flow while a driver has the
    // app on screen, so a marker is "last seen HH:mm" and goes grey after 15
    // minutes, and a stop with no pin in its systemGuide is NOT drawn (the line
    // under the map names how many are missing rather than staying quiet).
    mapOpen = true;
    base: 'sat' | 'map' = 'sat';
    /** "3 kantiner have no map pin yet" — never silently missing. */
    missingPinLabel = '';
    /** "2 drivers have not reported a position today" — same reason. */
    missingDriverLabel = '';

    private map: L.Map | null = null;
    private baseLayers: L.TileLayer[] = [];
    private driverLayer: L.LayerGroup | null = null;
    private stopLayer: L.LayerGroup | null = null;
    /** Guide pins by systemId, fetched once per system and reused. */
    private guidePoints: { [systemId: string]: { lat: number, lng: number, source: string } } = {};
    /** Which marker set the map was last zoomed to, so live updates do not
     *  yank the view away from an operator who panned somewhere. */
    private fittedKey = '';

    private runsSub: Subscription | null = null;
    private stopsSub: Subscription | null = null;
    private locationSubs = new Map<string, Subscription>();
    /** A run id passed in the URL (from the Overview screen) to open on arrival. */
    private pendingRunId: string | null = null;

    constructor(private svc: DeliveryService, private route: ActivatedRoute) {
        try { this.base = localStorage.getItem('mapBase') === 'map' ? 'map' : 'sat'; } catch { }
        try { this.mapOpen = localStorage.getItem('dispatchMap') !== 'closed'; } catch { }
    }

    ngOnInit() {
        // Deep link from the Overview screen: ?date=…&run=… opens the same day
        // with that run already expanded.
        const q = this.route.snapshot.queryParamMap;
        const d = q.get('date');
        if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) this.date = d;
        this.pendingRunId = q.get('run');
        this.subscribeRuns();
    }

    ngAfterViewInit() {
        setTimeout(() => this.ensureMap());
    }

    ngOnDestroy() {
        this.runsSub?.unsubscribe();
        this.stopsSub?.unsubscribe();
        this.locationSubs.forEach(s => s.unsubscribe());
        this.map?.remove();
        this.map = null;
    }

    // ---- date navigation -----------------------------------------------------

    onDateChange() { this.subscribeRuns(); }

    shiftDate(days: number) {
        this.date = moment(this.date, 'YYYY-MM-DD').add(days, 'day').format('YYYY-MM-DD');
        this.subscribeRuns();
    }

    goToday() {
        this.date = moment().format('YYYY-MM-DD');
        this.subscribeRuns();
    }

    isToday(): boolean { return this.date === moment().format('YYYY-MM-DD'); }

    // ---- data ----------------------------------------------------------------

    private subscribeRuns() {
        this.runsSub?.unsubscribe();
        this.loading = true;
        this.closeStops();
        this.runsSub = this.svc.runsForDate$(this.date).subscribe(runs => {
            this.runs = (runs || [])
                .map(r => this.toRunRow(r))
                .sort((a, b) => a.routeName.localeCompare(b.routeName));
            this.loading = false;
            this.watchDriverLocations();
            // Keep the open run open across live updates.
            if (this.expandedRunId && !this.runs.some(r => r.id === this.expandedRunId)) {
                this.closeStops();
            }
            if (this.pendingRunId) {
                const wanted = this.runs.find(r => r.id === this.pendingRunId);
                if (wanted) { this.pendingRunId = null; this.toggleRun(wanted); }
            }
            this.renderDrivers();
        }, () => { this.loading = false; });
    }

    // One live subscription per driver on today's board; drivers who drop off
    // the board keep their (cheap) subscription until the screen closes.
    private watchDriverLocations() {
        for (const run of this.runs) {
            const uid = run.driverUid;
            if (!uid || this.locationSubs.has(uid)) continue;
            this.locationSubs.set(uid, this.svc.driverLocation$(uid).subscribe(loc => {
                if (!loc || typeof loc.lat !== 'number') return;
                const at = Number(loc.at) || 0;
                this.lastSeen = {
                    ...this.lastSeen,
                    [uid]: {
                        label: 'Last seen ' + moment.unix(at).format('HH:mm'),
                        mapsUrl: `https://maps.google.com/?q=${loc.lat},${loc.lng}`,
                        stale: moment().unix() - at > 15 * 60,
                        lat: loc.lat,
                        lng: loc.lng,
                        at
                    }
                };
                this.renderDrivers();
            }));
        }
    }

    toggleRun(run: RunRow) {
        if (this.expandedRunId === run.id) {
            this.closeStops();
            return;
        }
        this.expandedRunId = run.id;
        this.stops = [];
        this.stopsLoading = true;
        this.stopsSub?.unsubscribe();
        this.stopsSub = this.svc.stopsForRun$(run.id).subscribe(stops => {
            this.stops = (stops || []).map(s => this.toStopRow(s));
            this.stopsLoading = false;
            this.loadGuidePoints();
            this.renderStops();
        }, () => { this.stopsLoading = false; });
    }

    private closeStops() {
        this.stopsSub?.unsubscribe();
        this.stopsSub = null;
        this.expandedRunId = null;
        this.stops = [];
        this.renderStops();
    }

    // ---- row shaping ----------------------------------------------------------

    private time(unix: any): string {
        return unix ? moment.unix(Number(unix)).format('HH:mm') : '';
    }

    private toRunRow(r: any): RunRow {
        const labels: any = {
            assigned: ['Not started', 'off'],
            picked_up: ['Boxes loaded', 'warn'],
            in_progress: ['On the road', 'warn'],
            completed: ['Completed', 'ok'],
            abandoned: ['Abandoned', 'bad']
        };
        const [statusLabel, statusClass] = labels[r.status] || [r.status || '—', 'off'];
        const total = Number(r.stopCount) || 0;
        const done = Number(r.completedStopCount) || 0;

        let whereLabel = '';
        if (r.status === 'in_progress') {
            whereLabel = r.atStopName
                ? `At ${r.atStopName}` + (r.atStopOrder ? ` (stop ${r.atStopOrder})` : '')
                : (r.lastDoneName ? `Driving — last done ${r.lastDoneName}` : '');
        }

        const mins = Number(r.expectedMinutesTotal) || 0;
        const expectedLabel = mins
            ? (mins >= 60 ? `~${Math.floor(mins / 60)}h ${mins % 60 ? mins % 60 + 'min' : ''} on site` : `~${mins} min on site`)
            : '';

        const from = r.takenOverFrom;
        const coveringLabel = from ? `covering for ${from.name || 'another driver'}` : '';

        return {
            coveringLabel,
            id: r.id,
            routeName: r.routeName || r.routeId || '—',
            driverUid: r.driverUid || null,
            driverName: r.driverName || 'No driver',
            status: r.status,
            statusLabel, statusClass,
            stopCount: total,
            completedStopCount: done,
            progressPct: total ? Math.round(done / total * 100) : 0,
            pickedUpAt: this.time(r.pickedUpAt),
            startedAt: this.time(r.startedAt),
            completedAt: this.time(r.completedAt),
            whereLabel,
            lastActivity: this.time(r.lastActivityAt),
            expectedLabel,
            issueCount: Number(r.issueCount) || 0
        };
    }

    private toStopRow(s: any): StopRow {
        const labels: any = {
            pending: ['Pending', 'off'],
            arrived: ['Arrived', 'warn'],
            filling: ['Filling', 'warn'],
            done: ['Done', 'ok'],
            skipped: ['Skipped', 'bad']
        };
        const [statusLabel, statusClass] = labels[s.status] || [s.status || '—', 'off'];

        const c = s.checks || {};
        const answered = ['door', 'selfTest', 'trays'].filter(k => typeof c[k] === 'boolean');
        const bad = answered.filter(k => c[k] === false);
        let checksLabel = '';
        if (answered.length) {
            checksLabel = bad.length
                ? 'Issue: ' + bad.map(k => k === 'selfTest' ? 'self-test' : k).join(', ')
                : 'Checks OK';
        }
        if (c.note) checksLabel = (checksLabel ? checksLabel + ' — ' : '') + c.note;

        const svinnCount = (s.svinn || []).reduce((sum: number, i: any) => sum + (Number(i.number) || 0), 0);

        return {
            id: s.id,
            systemId: s.systemId || '',
            order: s.order || 0,
            systemName: s.systemName || s.systemId,
            siteName: s.siteName || '',
            status: s.status,
            statusLabel: s.status === 'skipped' && s.skipReason
                ? `Skipped — ${s.skipReason}` : statusLabel,
            statusClass,
            arrivedAt: this.time(s.arrivedAt),
            completedAt: this.time(s.completedAt),
            fillCount: (s.fills || []).length,
            svinnCount,
            checksLabel,
            checksBad: bad.length > 0,
            note: s.note || ''
        };
    }


    // ---- map plumbing --------------------------------------------------------

    toggleMap() {
        this.mapOpen = !this.mapOpen;
        try { localStorage.setItem('dispatchMap', this.mapOpen ? 'open' : 'closed'); } catch { }
        if (this.mapOpen) {
            setTimeout(() => {
                this.ensureMap();
                this.map?.invalidateSize();
                this.fit(true);
            });
        }
    }

    private ensureMap() {
        if (this.map || !this.mapEl) return;
        this.map = L.map(this.mapEl.nativeElement, { zoomControl: true });
        this.applyBase();
        this.map.setView([59.91, 10.75], 5);              // no idea yet — Norway
        this.driverLayer = L.layerGroup().addTo(this.map);
        this.stopLayer = L.layerGroup().addTo(this.map);
        setTimeout(() => this.map?.invalidateSize(), 0);
        this.renderDrivers();
        this.renderStops();
    }

    // Aerial by default, exactly like the guide dialog and the driver app: a
    // yard, a loading dock and the door a van can reach are visible from above
    // and invisible on a street map. Remembered per browser under `mapBase`,
    // shared with the guide dialog on purpose — one choice, both maps.
    toggleBase() {
        this.base = this.base === 'sat' ? 'map' : 'sat';
        try { localStorage.setItem('mapBase', this.base); } catch { }
        this.applyBase();
    }

    private applyBase() {
        if (!this.map) return;
        this.baseLayers.forEach(l => l.remove());
        this.baseLayers = this.base === 'sat'
            ? [
                L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 21, maxNativeZoom: 19,
                    attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics'
                }),
                L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 21, maxNativeZoom: 19, attribution: ''
                })
            ]
            : [L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19, attribution: '&copy; OpenStreetMap'
            })];
        this.baseLayers.forEach(l => l.addTo(this.map!));
    }

    private pin(html: string, cls: string): L.DivIcon {
        return L.divIcon({
            className: '',
            html: `<div class="dm-pin ${cls}">${html}</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 28]
        });
    }

    // One marker per driver with a position today. Grey past 15 minutes — the
    // same threshold, and the same honesty, as the dd-seen link in the list.
    private renderDrivers() {
        if (!this.map || !this.driverLayer) return;
        this.driverLayer.clearLayers();
        // driverLocations holds ONE last-known point per driver, not a track —
        // so it says nothing about any day but this one. Drawing it on top of
        // last Tuesday's run would be a lie with a marker on it.
        if (!this.isToday()) {
            this.missingDriverLabel = 'Driver positions are only a last-known point, ' +
                'so they are not drawn for another day.';
            this.fit(false);
            return;
        }
        let silent = 0;
        for (const run of this.runs) {
            const uid = run.driverUid;
            if (!uid) continue;
            const seen = this.lastSeen[uid];
            if (!seen) { silent++; continue; }
            const stale = moment().unix() - seen.at > 15 * 60;
            const m = L.marker([seen.lat, seen.lng], {
                icon: this.pin('&#9679;', 'dm-driver' + (stale ? ' dm-stale' : '')),
                title: run.driverName,
                zIndexOffset: 1000
            });
            m.bindPopup(
                `<b>${this.escape(run.driverName)}</b><br>${this.escape(run.routeName)}<br>` +
                `${this.escape(seen.label)}${stale ? ' (older than 15 min)' : ''}`);
            m.addTo(this.driverLayer);
        }
        this.missingDriverLabel = silent
            ? `${silent} driver${silent === 1 ? ' has' : 's have'} not reported a position today ` +
              `— the app only sends one while it is open on screen.`
            : '';
        this.fit(false);
    }

    // The stops of the OPEN run, numbered in driving order and coloured by what
    // the driver has done with them.
    private renderStops() {
        if (!this.map || !this.stopLayer) return;
        this.stopLayer.clearLayers();
        if (!this.expandedRunId || !this.stops.length) {
            this.missingPinLabel = '';
            this.fit(false);
            return;
        }
        const missing: string[] = [];
        for (const s of this.stops) {
            const p = this.guidePoints[s.systemId];
            if (!p) { missing.push(s.systemName); continue; }
            const cls = s.status === 'done' ? 'dm-done'
                : s.status === 'skipped' ? 'dm-skipped'
                    : (s.status === 'arrived' || s.status === 'filling') ? 'dm-active' : 'dm-pending';
            const m = L.marker([p.lat, p.lng], { icon: this.pin(String(s.order), cls), title: s.systemName });
            const times = [s.arrivedAt ? 'arrived ' + s.arrivedAt : '', s.completedAt ? 'done ' + s.completedAt : '']
                .filter(t => t).join(' · ');
            m.bindPopup(
                `<b>${s.order}. ${this.escape(s.systemName)}</b><br>` +
                `${this.escape(s.statusLabel)}${times ? '<br>' + this.escape(times) : ''}` +
                (p.source === 'geo' ? '<br><i>approximate — geocoded address</i>' : ''));
            m.addTo(this.stopLayer);
        }
        this.missingPinLabel = missing.length
            ? `${missing.length} stop${missing.length === 1 ? '' : 's'} not on the map (no pin yet): ` +
              `${missing.slice(0, 4).join(', ')}${missing.length > 4 ? '…' : ''}. ` +
              `Set one on Routes → the place icon.`
            : '';
        this.fit(false);
    }

    // Coordinates come from the system's guide (parking pin, else the entrance
    // door, else a cached geocode). Fetched once per system and kept for the
    // life of the screen — the live stop snapshots must not re-read Firestore
    // every time a driver ticks a box.
    private async loadGuidePoints() {
        // Asked-for ids are remembered even when they came back empty, so the
        // stream of live stop snapshots does not re-ask for a kantine that
        // simply has no guide yet.
        const asked = Array.from(new Set(this.stops
            .map(s => s.systemId)
            .filter(id => id && !this.guideAsked.has(id))));
        if (!asked.length) return;
        asked.forEach(id => this.guideAsked.add(id));
        const found = await this.svc.getGuidePoints(asked);
        asked.forEach(id => { if (found[id]) this.guidePoints[id] = found[id]; });
        this.renderStops();
        this.fit(true);
    }
    private guideAsked = new Set<string>();

    // Zoom to everything drawn. Only when the marker set actually changed, so
    // an operator who panned to a driver keeps their view through live updates.
    private fit(force: boolean) {
        if (!this.map) return;
        const pts: L.LatLngExpression[] = [];
        for (const s of this.stops) {
            const p = this.guidePoints[s.systemId];
            if (p) pts.push([p.lat, p.lng]);
        }
        if (this.isToday()) {
            for (const run of this.runs) {
                const seen = run.driverUid ? this.lastSeen[run.driverUid] : null;
                if (seen) pts.push([seen.lat, seen.lng]);
            }
        }
        if (!pts.length) return;
        const key = (this.expandedRunId || 'all') + ':' + pts.length;
        if (!force && key === this.fittedKey) return;
        this.fittedKey = key;
        if (pts.length === 1) this.map.setView(pts[0], 15);
        else this.map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 16 });
    }

    private escape(s: string): string {
        return (s || '').replace(/[&<>"]/g, c =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
    }

    trackByRun(_: number, r: RunRow) { return r.id; }
    trackByStop(_: number, s: StopRow) { return s.id; }
}
